import { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from './AuthContext.jsx';
import { recordFocusSession } from '../lib/focusLog.js';

// Offline queue: focus POSTs that fail (no network / server hiccup) are stored
// and retried when the app comes back online, so no session is ever lost.
const PENDING_KEY = 'pending_focus_posts';
const readPending = () => { try { const q = JSON.parse(localStorage.getItem(PENDING_KEY) || '[]'); return Array.isArray(q) ? q : []; } catch { return []; } };
const pushPending = (payload) => { try { localStorage.setItem(PENDING_KEY, JSON.stringify([...readPending(), payload].slice(-50))); } catch {} };
async function flushPending(apiFetch) {
  const q = readPending();
  if (!q.length) return;
  const rest = [];
  for (const item of q) {
    try {
      const res = await apiFetch('/health/focus', { method: 'POST', body: JSON.stringify(item) });
      if (!res || !res.ok) rest.push(item);
    } catch { rest.push(item); }
  }
  try { localStorage.setItem(PENDING_KEY, JSON.stringify(rest)); } catch {}
}

// One global focus timer: free by default, optionally labelled ("на что?").
// Survives tab switches (lives above the router) and page reloads (localStorage).
const Ctx = createContext(null);
export const useFocusTimer = () => useContext(Ctx) || {};
const LS = 'focus_timer_v1';

export function TimerProvider({ children }) {
  const { apiFetch } = useAuth();

  // Retry queued focus sessions on start and whenever connectivity returns.
  useEffect(() => {
    flushPending(apiFetch);
    const h = () => flushPending(apiFetch);
    window.addEventListener('online', h);
    return () => window.removeEventListener('online', h);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [endsAt, setEndsAt] = useState(null);            // epoch ms of finish (null = not counting down)
  const [duration, setDuration] = useState(25 * 60);     // chosen length, seconds
  // Секундомер: открытая сессия «до остановки». Технически — таймер с потолком
  // 8ч (SW_CAP): UI показывает elapsed, logNow пишет фактически прошедшее.
  const SW_CAP = 8 * 3600;
  const [swMode, setSwMode] = useState(() => { try { return localStorage.getItem('ft_sw') === '1'; } catch { return false; } });
  useEffect(() => { try { localStorage.setItem('ft_sw', swMode ? '1' : '0'); } catch {} }, [swMode]);
  const [pausedRemaining, setPausedRemaining] = useState(null); // sec left while paused (null = not paused)
  const [label, setLabel] = useState('');
  const [goal, setGoal] = useState(null); // {id, title} | null — optional linked goal
  const [now, setNow] = useState(Date.now());
  const [todayCount, setTodayCount] = useState(0);
  const [todayMin, setTodayMin] = useState(0);
  const finishedRef = useRef(true);
  // Last finished session — powers the one-tap «Продолжить» after the bell.
  const [lastSession, setLastSession] = useState(() => {
    try { return JSON.parse(localStorage.getItem(LS) || '{}').lastSession || null; } catch { return null; }
  });
  // Session id: constant across pause/resume, synced with the state — lets a
  // device check whether another one already logged this exact session.
  const [sid, setSid] = useState(() => {
    try { return JSON.parse(localStorage.getItem(LS) || '{}').sid || null; } catch { return null; }
  });
  // updatedAt for cross-device conflict resolution. Bumped ONLY by user actions
  // (start/pause/resume/…): if the persist effect bumped it on every render,
  // the local blob would always look newer and remote state could never win.
  const dirtyAtRef = useRef((() => {
    try { return JSON.parse(localStorage.getItem(LS) || '{}').updatedAt || 0; } catch { return 0; }
  })());
  const touch = () => { dirtyAtRef.current = Date.now(); };

  const loadToday = useCallback(async () => {
    try {
      const r = await apiFetch(`/health/focus/today?from=${encodeURIComponent(new Date(new Date().setHours(0,0,0,0)).toISOString())}`);
      if (r && r.ok) { const d = await r.json(); setTodayCount(d.count || 0); setTodayMin(d.minutes || 0); }
    } catch { /* ignore */ }
  }, [apiFetch]);

  // restore persisted state once
  useEffect(() => {
    try {
      const raw = localStorage.getItem(LS);
      if (raw) {
        const s = JSON.parse(raw);
        if (s.duration) setDuration(s.duration);
        if (s.label) setLabel(s.label);
        if (s.goal) setGoal(s.goal);
        if (s.sid) setSid(s.sid);
        if (s.pausedRemaining != null) { setPausedRemaining(s.pausedRemaining); finishedRef.current = false; }
        else if (s.endsAt && s.endsAt > Date.now()) { setEndsAt(s.endsAt); finishedRef.current = false; }
        else if (s.endsAt) finalizeMissed(s); // звонок случился, пока приложение было закрыто
      }
    } catch { /* ignore */ }
    loadToday();
  }, [loadToday]);

  // persist (+ notify ThemeSync so the timer state rides to other devices)
  useEffect(() => {
    try {
      localStorage.setItem(LS, JSON.stringify({ endsAt, duration, pausedRemaining, label, goal, lastSession, sid, updatedAt: dirtyAtRef.current }));
      window.dispatchEvent(new Event('focus_timer_changed'));
    } catch { /* ignore */ }
  }, [endsAt, duration, pausedRemaining, label, goal, lastSession, sid]);

  // Adopt a newer timer state synced from another device (via ui_prefs).
  useEffect(() => {
    const h = () => {
      try {
        const s2 = JSON.parse(localStorage.getItem(LS) || 'null');
        if (!s2) return;
        if (s2.duration) setDuration(s2.duration);
        setLabel(s2.label || '');
        setGoal(s2.goal || null);
        setLastSession(s2.lastSession || null);
        setSid(s2.sid || null);
        dirtyAtRef.current = s2.updatedAt || dirtyAtRef.current;
        if (s2.pausedRemaining != null) { setPausedRemaining(s2.pausedRemaining); setEndsAt(null); finishedRef.current = false; }
        else if (s2.endsAt && s2.endsAt > Date.now()) { setNow(Date.now()); setEndsAt(s2.endsAt); setPausedRemaining(null); finishedRef.current = false; }
        else { setEndsAt(null); setPausedRemaining(null); finishedRef.current = true; finalizeMissed(s2); }
      } catch { /* ignore */ }
    };
    window.addEventListener('focus_timer_remote', h);
    return () => window.removeEventListener('focus_timer_remote', h);
  }, []);

  // tick while counting down
  useEffect(() => {
    if (endsAt == null) return undefined;
    const id = setInterval(() => setNow(Date.now()), 250);
    return () => clearInterval(id);
  }, [endsAt]);

  const running = endsAt != null || pausedRemaining != null;
  const paused = pausedRemaining != null;
  const remaining = paused
    ? pausedRemaining
    : (endsAt != null ? Math.max(0, Math.round((endsAt - now) / 1000)) : duration);

  const beep = () => {
    try {
      const AC = window.AudioContext || window.webkitAudioContext; if (!AC) return;
      const ac = new AC();
      [0, 0.28].forEach((t0) => {
        const o = ac.createOscillator(), g = ac.createGain();
        o.connect(g); g.connect(ac.destination); o.type = 'sine'; o.frequency.value = 880;
        const t = ac.currentTime + t0;
        g.gain.setValueAtTime(0.0001, t); g.gain.exponentialRampToValueAtTime(0.3, t + 0.02);
        g.gain.exponentialRampToValueAtTime(0.0001, t + 0.22); o.start(t); o.stop(t + 0.24);
      });
    } catch { /* ignore */ }
  };

  // Log a finished session unless another device already claimed this sid.
  // The claim check is a quick GET /settings (where the synced timer state
  // lives); on any doubt we log — the server side is idempotent enough and
  // losing a session is worse than a rare duplicate.
  const claimAndLog = useCallback(async (payload, sessionId) => {
    try {
      if (sessionId) {
        const r = await apiFetch('/settings');
        if (r && r.ok) {
          const d = await r.json().catch(() => null);
          const remoteSid = d && d.ui_prefs && d.ui_prefs.focusTimer && d.ui_prefs.focusTimer.lastSession && d.ui_prefs.focusTimer.lastSession.sid;
          if (remoteSid && remoteSid === sessionId) return; // другой девайс уже записал
        }
      }
    } catch { /* сеть недоступна — просто логируем */ }
    recordFocusSession(); // local per-day ledger → counts toward Activity even offline
    try {
      const res = await apiFetch('/health/focus', { method: 'POST', body: JSON.stringify(payload) });
      if (!res || !res.ok) pushPending(payload);
      await loadToday();
    } catch { pushPending(payload); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiFetch, loadToday]);

  // A bell that rang while the app was closed: the stored state still has an
  // expired endsAt. Log it now (once — guarded by sid claim + lastSession).
  const finalizeMissed = (st) => {
    if (!st || !st.endsAt || st.endsAt > Date.now()) return;
    const sd = st.sid || null;
    if (sd && st.lastSession && st.lastSession.sid === sd) return; // уже записана
    const payload = { durationSec: st.duration || duration, label: st.label || null, goalId: st.goal && st.goal.id ? st.goal.id : null, completed: true };
    // В секундомере duration — это потолок (8ч), а не отработанное время:
    // из-за этого «Продолжить» предлагало 480 минут. Берём фактическое.
    setLastSession({ durationSec: spentOf(st), label: st.label || '', goal: st.goal || null, at: Date.now(), sid: sd });
    touch();
    claimAndLog(payload, sd);
  };

  const onFinish = useCallback(async () => {
    const dur = duration, lbl = label, gid = goal && goal.id ? goal.id : null;
    setEndsAt(null); setPausedRemaining(null);
    beep();
    try { if (navigator.vibrate) navigator.vibrate([200, 80, 200]); } catch { /* ignore */ }
    try { window.dispatchEvent(new CustomEvent('focus_done', { detail: { label: lbl } })); } catch { /* ignore */ }
    const mySid = sid;
    setLastSession({ durationSec: dur, label: lbl, goal: goal || null, at: Date.now(), sid: mySid });
    touch();
    const payload = { durationSec: dur, label: lbl || null, goalId: gid, completed: true };
    await claimAndLog(payload, mySid);
  }, [duration, label, goal, sid, claimAndLog]);

  // finish detection
  useEffect(() => {
    if (endsAt != null && !paused && remaining <= 0 && !finishedRef.current) {
      finishedRef.current = true;
      onFinish();
    }
  }, [remaining, endsAt, paused, onFinish]);

  const start = (sec, lbl, g = null, opts = {}) => {
    // Если таймер уже идёт — сначала закрываем текущую сессию, потом начинаем
    // новую. Без этого второй старт (другое устройство, восстановленное
    // состояние, повторный тап) давал две ПЕРЕСЕКАЮЩИЕСЯ сессии, и день
    // считался дважды. Сервер тоже подрезает пересечения — это второй рубеж.
    if (endsAt != null && !finishedRef.current) {
      const rem = pausedRemaining != null ? pausedRemaining : Math.max(0, Math.round((endsAt - Date.now()) / 1000));
      const spent = Math.max(0, duration - rem);
      if (spent >= 30) {
        const payload = { durationSec: spent, label: label || null, goalId: goal && goal.id ? goal.id : null, completed: true };
        claimAndLog(payload, sid);
        recordFocusSession();
      }
      finishedRef.current = true;
    }
    const sw = !!opts.stopwatch;
    setSwMode(sw);
    const s = sw ? SW_CAP : Math.max(1, Math.round(sec || duration));
    setDuration(s);
    if (lbl != null) setLabel(lbl);
    setGoal(g || null);
    setSid('s' + Date.now().toString(36) + Math.random().toString(36).slice(2, 7));
    finishedRef.current = false;
    setPausedRemaining(null);
    setNow(Date.now());
    setEndsAt(Date.now() + s * 1000);
    touch();
  };
  const pause = () => { if (endsAt != null && !paused) { setPausedRemaining(remaining); setEndsAt(null); touch(); } };
  const resume = () => { if (paused) { finishedRef.current = false; setNow(Date.now()); setEndsAt(Date.now() + pausedRemaining * 1000); setPausedRemaining(null); touch(); } };
  const reset = () => { finishedRef.current = true; setEndsAt(null); setPausedRemaining(null); touch(); };

  // Stop now and record the time already spent (so partial sessions still count).
  // Сколько реально отработано: для секундомера это elapsed, для таймера —
  // заданная длительность минус остаток.
  const spentOf = (st) => {
    const d = st?.duration || duration || 0;
    if (st?.swMode || swMode) return Math.max(0, Math.round((st?.elapsed ?? (d - remaining)) || 0));
    return d;
  };

  const logNow = useCallback(async () => {
    const rem = pausedRemaining != null
      ? pausedRemaining
      : (endsAt != null ? Math.max(0, Math.round((endsAt - Date.now()) / 1000)) : duration);
    const spent = Math.max(0, duration - rem);
    const gid = goal && goal.id ? goal.id : null, lbl = label;
    finishedRef.current = true; setEndsAt(null); setPausedRemaining(null);
    if (spent < 30) return; // too short to be worth logging
    beep();
    try { if (navigator.vibrate) navigator.vibrate(80); } catch { /* ignore */ }
    try { window.dispatchEvent(new CustomEvent('focus_done', { detail: { label: lbl, sec: spent } })); } catch { /* ignore */ }
    const mySid = sid;
    setLastSession({ durationSec: duration, label: lbl, goal: goal || null, at: Date.now(), sid: mySid });
    touch();
    const payload = { durationSec: spent, label: lbl || null, goalId: gid, completed: true };
    await claimAndLog(payload, mySid);
  }, [pausedRemaining, endsAt, duration, goal, label, sid, claimAndLog]);

  // One tap after the bell: same length, same label, same goal — no re-setup.
  const continueLast = () => { if (lastSession) start(lastSession.durationSec, lastSession.label || '', lastSession.goal || null); };

  // Забыл запустить таймер — можно записать время вручную (минуты + подпись/цель).
  // Идёт тем же путём, что обычная сессия: попадёт и в «Сегодня», и в статистику.
  const logManual = useCallback(async (minutes, lbl = '', g = null, opts = {}) => {
    const sec = Math.max(60, Math.round((Number(minutes) || 0) * 60));
    // manual: true — сервер не подрезает запись под предыдущую сессию.
    // Интервал (startedAt/endedAt) передаём, только если человек указал его сам;
    // иначе запись живёт без времени: «50 минут чтения», и всё.
    const payload = {
      durationSec: sec, label: lbl || null, goalId: g && g.id ? g.id : null,
      completed: true, manual: true,
      startedAt: opts.startedAt || null, endedAt: opts.endedAt || null,
    };
    recordFocusSession();
    try {
      const res = await apiFetch('/health/focus', { method: 'POST', body: JSON.stringify(payload) });
      if (!res || !res.ok) return false; // очередь не используем: повтор плодил дубли
      await loadToday();
      return true;
    } catch { return false; }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiFetch, loadToday]);

  // Правка подписи сессии из ленты.
  // Меняем подпись и/или привязку к цели. goalId === null снимает привязку,
  // undefined — оставляет как было.
  const renameSession = useCallback(async (id, label, goalId) => {
    try {
      const payload = { label };
      if (goalId !== undefined) payload.goalId = goalId;
      const res = await apiFetch(`/health/focus/${id}`, { method: 'PATCH', body: JSON.stringify(payload) });
      if (res?.ok) return await res.json();
    } catch { /* ignore */ }
    return null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiFetch]);

  // Удаление сессии — из ленты в Целях; счётчик «сегодня» пересчитывается.
  const deleteSession = useCallback(async (id) => {
    try {
      const res = await apiFetch(`/health/focus/${id}`, { method: 'DELETE' });
      if (res?.ok) { await loadToday(); return true; }
    } catch { /* ignore */ }
    return false;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiFetch, loadToday]);

  return (
    <Ctx.Provider value={{ running, paused, remaining, duration, label, elapsed: duration - remaining, setLabel: (v) => { setLabel(v); touch(); }, goal, setGoal: (v) => { setGoal(v); touch(); },
                           todayCount, todayMin, start, pause, resume, reset, logNow, refreshToday: loadToday,
                           lastSession, continueLast, swMode, setSwMode, logManual, deleteSession, renameSession }}>
      {children}
    </Ctx.Provider>
  );
}
